import { createSelector } from 'reselect'
import { receivedServerData } from "../../../Common/Data/DataFetcher"
import { denormalize } from 'normalizr';
import { buildTitleForColumnValues, getColumnsInfoNamespace } from "../../../Common/TableColumnsSelector/Data"
import CustomersApi from "../../api/CustomersApi"
import RequestApi from "../../api/RequestApi"
import JobApi from "../../../../Resources/Job/JobApi"
import BetaEstimatesAPI from 'resources/Estimate/BetaEstimatesAPI'
import { getEntities } from 'resources/EntityData';
import { getCompositeSchemaKey } from 'components/Common/Data/DataFetcherSuperUtils';
import { isEntityPresentSelector } from 'components/Common/Data/DataFetcherEntitiesHelper';
import { EmptyObject } from 'resources/EmptyDefaults';

import {
  ENTITY_CUSTOMER_JOBS_LIST,
  ENTITY_CUSTOMER_ESTIMATES_LIST,
  customerJobsListSchema,
  customerEstimatesListSchema,
  ENTITY_CUSTOMER_JOBS,
} from 'schema/CustomerRequestsSchema';

import {
  customer,
  CUSTOMER_NAMESPACE,
  getCustomerPaymentStats,
} from "../CustomerData"
import {
    CUSTOMER_ESTIMATES_TABLE,
    CUSTOMER_JOBS_TABLE,
    defaultTableSize,
    customerJobsDefaultColumnValues,
    customerEstimatesDefaultColumnValues,
} from "./RequestsTableConfiguration"

export const CUSTOMER_JOBS_PAGINATION_ID = 'customer_jobs';
export const CUSTOMER_ESTIMATES_PAGINATION_ID = 'customer_estimates';

export const getJobs = createSelector(
    customer,
    (customer) => (customer.jobs || [])
)

export const getSelectedJobIds = createSelector(
    customer,
    (customer) => (customer.selected_job_ids || [])
)

export const getSelectedJobs = createSelector(
    [getJobs, getSelectedJobIds],
    (jobs, ids) => (jobs.filter(job => ids.includes(job.id)))
)

export const getSelectedJobUuids = createSelector(
  getSelectedJobs,
  jobs => jobs.map(job => job.uuid)
)

export const getSelectedTotalOutstandingBalance = createSelector(
    getSelectedJobs,
    (jobs) => {
        let balance = 0
        jobs.map(job => {
            if (job.outstanding_balance > 0) {
                balance += job.outstanding_balance
            }
        })
        return balance / 100.0
    }
)

export const getSelectedJobInvoiceNumbers = createSelector(
    getSelectedJobs,
    (jobs) => {
        let invoiceIds = []
        jobs.map(job => invoiceIds.push(job.invoice_number))
        return invoiceIds
    }

)

export const getBillingAddress = createSelector(
    customer,
    (customer) => (customer.billing_address || {})
)

export const getRequestAddresses = createSelector(
    customer,
    (customer) => (
        customer.job_addresses || []
    )
)

export const getFilterAddresses = createSelector(
    customer,
    (customer) => (
        customer.filter_addresses || []
    )
)

export const getColumns = state => state[getColumnsInfoNamespace()] || {}

export const getJobsColumnValues = createSelector(
    getColumns,
    (columns) => {
        return buildTitleForColumnValues(customerJobsDefaultColumnValues, columns[CUSTOMER_JOBS_TABLE] || [])
    }
)

export const getEstimatesColumnValues = createSelector(
    getColumns,
    (columns) => {
        return buildTitleForColumnValues(customerEstimatesDefaultColumnValues, columns[CUSTOMER_ESTIMATES_TABLE] || [])
    }
)

export const fetchJobHistory = {
    fetchingApiFunc: RequestApi.index,
    selector: getJobs,
    paramsFromProps: (props) => ([props.customerId, 1, defaultTableSize, props.filterParams]),
    dataNamespace: CUSTOMER_NAMESPACE,
    alwaysFetch: true
}

export const fetchRequestAddresses = {
    fetchingApiFunc: CustomersApi.getRequestAddresses,
    selector: getRequestAddresses,
    paramsFromProps: (props) => ([props.customerId]),
    dataNamespace: CUSTOMER_NAMESPACE
}

export function updateSelectedJobIds(ids) {
    return receivedServerData({selected_job_ids: ids}, CUSTOMER_NAMESPACE)
}

export function updateFilterAddresses(addresses) {
    return receivedServerData({filter_addresses: addresses}, CUSTOMER_NAMESPACE)
}

export function receivedPaymentStats(paymentStats) {
    return receivedServerData(paymentStats, CUSTOMER_NAMESPACE)
}

const getCustomerJobs = createSelector(
  getEntities,
  (_state, { paginationData } = {}) => getCompositeSchemaKey(paginationData),
  (state, { paginationData } = {}) => isEntityPresentSelector(state, {
    namespace: ENTITY_CUSTOMER_JOBS_LIST,
    key: getCompositeSchemaKey(paginationData),
  }),
  (entities, schemaKey, isCustomerJobsListPresent) => {
    if (!isCustomerJobsListPresent) return EmptyObject;
    return denormalize(schemaKey, customerJobsListSchema, entities) || EmptyObject;
  },
);

const getAllViewedJobs = createSelector(
  getEntities,
  (entities) => {
    const allJobs = entities[ENTITY_CUSTOMER_JOBS] || EmptyObject;
    return Object.values(allJobs) || []
  },
);

export const getEntryJobForBatchInvoice = createSelector(
  getEntities,
  getSelectedJobIds,
  (entities, jobUuids) => {
    const allJobUuids = Object.values(entities[ENTITY_CUSTOMER_JOBS] || {});
    return allJobUuids.filter(job => jobUuids.includes(job.id))[0] || EmptyObject;
  },
);

const getCustomerEstimates = createSelector(
  getEntities,
  (_state, { paginationData } = {}) => getCompositeSchemaKey(paginationData),
  (state, { paginationData } = {}) => isEntityPresentSelector(state, {
    namespace: ENTITY_CUSTOMER_ESTIMATES_LIST,
    key: getCompositeSchemaKey(paginationData),
  }),
  (entities, schemaKey, isCustomerEstimatesListPresent) => {
    if (!isCustomerEstimatesListPresent) return EmptyObject;
    return denormalize(schemaKey, customerEstimatesListSchema, entities) || EmptyObject;
  },
);

export const fetchCustomerJobs = {
  fetchingApiFunc: JobApi.index,
  paramsFromProps: ({paginationData} = {}) => [{ expand: ['service_address', 'customer', 'notes', 'pros'], ...paginationData}],
  schema: customerJobsListSchema,
  selector: getCustomerJobs
};

export const fetchCustomerEstimates = {
  fetchingApiFunc: BetaEstimatesAPI.getEstimates,
  paramsFromProps: ({paginationData} = {}) => [{...paginationData}],
  schema: customerEstimatesListSchema,
  selector: getCustomerEstimates
};

const getSelectedCustomerJobs = createSelector(
  [getAllViewedJobs, getSelectedJobIds],
  (jobs, ids) => (jobs.filter(job => ids.includes(job.id)))
);

export const getCustomerOutstandingBalance = createSelector(
    getSelectedCustomerJobs,
    (jobs) => {
        let balance = 0
        jobs?.forEach(job => {
            if (job.outstanding_balance > 0) {
                balance += job.outstanding_balance
            }
        })
        return balance / 100.0
    }
)

export const getCustomerOutstandingBalanceFromSelectedJobs = createSelector(
  getCustomerPaymentStats,
  (paymentStats) => paymentStats.selected_jobs_balance || 0,
);

export const getCustomerSelectedInvoiceNumbers = createSelector(
  getCustomerPaymentStats,
  (paymentStats) => paymentStats.selected_jobs_invoice_numbers || [],
);
